/**
 * Google Analytics 4 helper — strict opt-in.
 * The gtag script is injected ONLY after the visitor explicitly accepts
 * analytics (see components/ConsentBanner.jsx). The choice is persisted in
 * localStorage; declining means GA never loads (no cookies, no requests).
 * We intentionally disable auto page_view (`send_page_view: false`)
 * and fire manual page_view events on every react-router change.
 */

export const GA_MEASUREMENT_ID = "G-45WM532HE3";
const CONSENT_KEY = "hh-analytics-consent"; // "granted" | "denied" | null
const GA_DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;

export function getAnalyticsConsent() {
    try {
        return localStorage.getItem(CONSENT_KEY); // null = undecided
    } catch {
        return "denied"; // storage unavailable (private mode) — safest default
    }
}

export function setAnalyticsConsent(value) {
    try {
        localStorage.setItem(CONSENT_KEY, value);
    } catch { /* storage unavailable — treat as session-only */ }
    if (value === "granted") {
        window[GA_DISABLE_FLAG] = false;
        initAnalytics();
    } else {
        // Standard GA opt-out flag — stops any already-loaded gtag from sending
        window[GA_DISABLE_FLAG] = true;
    }
}

/** Inject the gtag script + config. No-op unless consent === "granted". */
export function initAnalytics() {
    if (getAnalyticsConsent() !== "granted") return;
    if (typeof window === "undefined" || document.getElementById("ga4-script")) return;
    window[GA_DISABLE_FLAG] = false;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
    const s = document.createElement("script");
    s.id = "ga4-script";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);
}

function isReady() {
    return (
        typeof window !== "undefined" &&
        typeof window.gtag === "function" &&
        getAnalyticsConsent() === "granted" &&
        !window[GA_DISABLE_FLAG]
    );
}

/** Fire a manual page_view. Called on every route change. */
export function trackPageView(pathname) {
    if (!isReady()) return;
    const path = pathname || window.location.pathname + window.location.search;
    window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
        send_to: GA_MEASUREMENT_ID,
    });
}

/**
 * Fire a custom event.
 * Example: trackEvent("contact_submit", { project_type: "Custom Home" })
 */
export function trackEvent(name, params = {}) {
    if (!isReady()) return;
    window.gtag("event", name, params);
}

/* --- Convenience helpers for the events we care about --- */

export const track = {
    // Conversion — someone submitted the contact form successfully
    contactSubmit: (projectType) =>
        trackEvent("contact_form_submit", {
            event_category: "lead",
            event_label: projectType || "unspecified",
            project_type: projectType || "unspecified",
        }),
    contactError: (message) =>
        trackEvent("contact_form_error", {
            event_category: "lead",
            event_label: message || "",
        }),
    // Phone / email / social clicks (all counted as leads)
    phoneClick: (source) =>
        trackEvent("phone_click", {
            event_category: "lead",
            event_label: source || "unknown",
            source: source || "unknown",
        }),
    emailClick: (source) =>
        trackEvent("email_click", {
            event_category: "lead",
            event_label: source || "unknown",
            source: source || "unknown",
        }),
    facebookClick: (source) =>
        trackEvent("facebook_click", {
            event_category: "outbound",
            event_label: source || "unknown",
        }),
    // Major CTA clicks (button label + originating section)
    ctaClick: (label, section) =>
        trackEvent("cta_click", {
            event_category: "engagement",
            event_label: label,
            cta_label: label,
            cta_section: section || "unknown",
        }),
    // Gallery interactions
    galleryOpen: (photoId) =>
        trackEvent("gallery_photo_open", {
            event_category: "engagement",
            event_label: photoId || "",
        }),
};
